import { COLORS } from '@btime/constants'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { HorizontalLine } from '../../../../../../../components/shapes/Line'
import { SmallText, Title } from '../../../../../../../packages/kiti/components/presentation/Text'
import { canRenderInputForType } from '../_helpers/hideInputForType'
import { useChecklistFormActions, useSelectedQuestion } from '../../../../_form/ChecklistFormHandler'
import CheckboxInput from '../../../../../../../components/form/CheckboxInput'
import FormItem from '../../../../../../../components/form/FormItem'
import Select from '../../../../../../../packages/kiti/components/form/Select'
import Separator from '../../../../../../../packages/kiti/components/helpers/Separator'
import Link from '../../../../../../../packages/kiti/components/actions/Link'
import { fetchFieldsFromApi, getFieldsFromCache } from '../../../../../../../utils/helpers/fields'
import { usePlaceTypes } from '../../../../../../../utils/helpers/placeTypes'

const AUTOFILL_SOURCE_USER = 'user'
const AUTOFILL_SOURCE_PLACE = 'place'
const ORIGIN_CONTRACT_ALIASES = ['contracts', 'contract']

function getOriginForSource(source, allPlaces) {
  const raw = source?.id ?? source
  const key = raw != null ? String(raw).toLowerCase() : null
  if (key === AUTOFILL_SOURCE_USER) return ORIGIN_CONTRACT_ALIASES
  if (key === AUTOFILL_SOURCE_PLACE) return allPlaces
  return []
}

const normalizeSource = source => (source && typeof source === 'object' && 'id' in source ? source.id : source)

const AutofillFieldSelect = ({ source, origin, value, onChange, placeholder }) => {
  const sourceRaw = normalizeSource(source)
  const sourceId = sourceRaw != null ? String(sourceRaw).toLowerCase() : null
  const [selectedLabel, setSelectedLabel] = useState('')

  const cachedLabel = useMemo(() => {
    if (value == null || value === '' || !origin?.length) return ''
    const fields = getFieldsFromCache(origin, undefined)
    const field = (fields || []).find(f => String(f.id) === String(value))
    return field?.name ?? ''
  }, [value, origin])

  const loadOptions = useCallback(
    async (search, page) => {
      if (!origin?.length) return []
      const fields = await fetchFieldsFromApi(origin, undefined)
      const available = (fields || []).filter(
        f =>
          f.type !== 'gallery' &&
          (sourceId === AUTOFILL_SOURCE_USER ? ORIGIN_CONTRACT_ALIASES.includes(f.origin) : true)
      )
      return available.map(f => ({ id: f.id, name: f.name }))
    },
    [sourceId, origin]
  )

  useEffect(() => {
    if (value == null || value === '' || !sourceId || !origin?.length) {
      setSelectedLabel('')
      return
    }
    let cancelled = false
    fetchFieldsFromApi(origin, undefined).then(fields => {
      if (cancelled) return
      const field = (fields || []).find(f => String(f.id) === String(value))
      setSelectedLabel(field?.name ?? '')
    })
    return () => { cancelled = true }
  }, [sourceId, value, origin])

  const displayLabel =
    selectedLabel || cachedLabel || (value != null && value !== '' ? '...' : undefined)

  return (
    <Select
      key={sourceId}
      options={loadOptions}
      value={value}
      selectedLabel={displayLabel || undefined}
      onChange={onChange}
      placeholder={placeholder}
      idField="id"
      labelField="name"
      autoComplete="off"
    />
  )
}

const AutofillSection = () => {
  const { t } = useTranslation('editChecklist')
  const { handleSelectedQuestionChangeOnPath } = useChecklistFormActions()
  const selectedQuestion = useSelectedQuestion()
  const [placeTypes] = usePlaceTypes()

  const shouldRender = canRenderInputForType(selectedQuestion?.questionType?.name)
  const shouldRenderAutofill = shouldRender('autofill')

  const autofill = selectedQuestion?.data?.autofill || null
  const source = normalizeSource(autofill?.source ?? null)
  const sourceForSelect = source != null ? String(source).toLowerCase() : null
  const fieldId = autofill?.fieldId ?? autofill?.field_id ?? null
  const editable = autofill?.editable ?? true
  const updateBase = autofill?.updateBase ?? autofill?.update_base ?? false

  const allPlaces = useMemo(
    () => (placeTypes || []).map(item => item.key).filter(Boolean) || [],
    [placeTypes]
  )

  const sourceOptions = useMemo(
    () => [
      { id: AUTOFILL_SOURCE_USER, name: t('autofill.sourceUser') },
      { id: AUTOFILL_SOURCE_PLACE, name: t('autofill.sourcePlace') }
    ],
    [t]
  )

  const fieldSelectOrigin = useMemo(
    () => getOriginForSource(sourceForSelect, allPlaces),
    [sourceForSelect, allPlaces]
  )

  const handleSourceChange = useCallback(
    value => {
      if (!value) {
        if (autofill?.source != null && autofill?.fieldId != null) return
        handleSelectedQuestionChangeOnPath(['data', 'autofill'], null)
        return
      }
      const raw = normalizeSource(value)
      const sourceValue = raw != null ? String(raw).toLowerCase() : raw
      handleSelectedQuestionChangeOnPath(['data', 'autofill'], {
        ...autofill,
        source: sourceValue,
        fieldId: null,
        field_id: null
      })
    },
    [autofill, handleSelectedQuestionChangeOnPath]
  )

  const handleFieldChange = useCallback(
    value => {
      if (!value) {
        if (autofill?.source != null && autofill?.fieldId != null) return
        handleSelectedQuestionChangeOnPath(
          ['data', 'autofill'],
          sourceForSelect ? { ...autofill, source: sourceForSelect, fieldId: null, field_id: null } : null
        )
        return
      }
      const id = value && typeof value === 'object' && 'id' in value ? value.id : value
      handleSelectedQuestionChangeOnPath(['data', 'autofill'], {
        ...autofill,
        source: sourceForSelect,
        fieldId: id,
        field_id: id
      })
    },
    [autofill, sourceForSelect, handleSelectedQuestionChangeOnPath]
  )

  const handleEditableChange = useCallback(
    value => {
      handleSelectedQuestionChangeOnPath(['data', 'autofill'], {
        ...autofill,
        source: sourceForSelect,
        fieldId,
        field_id: fieldId,
        editable: value,
        updateBase
      })
    },
    [autofill, sourceForSelect, fieldId, updateBase, handleSelectedQuestionChangeOnPath]
  )

  const handleUpdateBaseChange = useCallback(
    value => {
      handleSelectedQuestionChangeOnPath(['data', 'autofill'], {
        ...autofill,
        source: sourceForSelect,
        fieldId,
        field_id: fieldId,
        editable,
        updateBase: value
      })
    },
    [autofill, sourceForSelect, fieldId, editable, handleSelectedQuestionChangeOnPath]
  )

  const clearAutofill = useCallback(() => {
    handleSelectedQuestionChangeOnPath(['data', 'autofill'], null)
  }, [handleSelectedQuestionChangeOnPath])

  if (!shouldRenderAutofill) return null

  return (
    <>
      <Separator weight={6} />
      <Title size="h6" color={COLORS.dark4}>
        {t('autofill.title')}
      </Title>
      <HorizontalLine thin lineColor={COLORS.light2} />
      <Separator weight={2} />

      <FormItem label={t('autofill.dataSource')} weight={2}>
        <Select
          options={sourceOptions}
          value={sourceForSelect}
          onChange={handleSourceChange}
          placeholder={t('autofill.selectSource')}
          idField="id"
          labelField="name"
          autoComplete="off"
        />
      </FormItem>

      {sourceForSelect && (
        <>
          <FormItem label={t('autofill.field')} weight={2}>
            <AutofillFieldSelect
              source={sourceForSelect}
              origin={fieldSelectOrigin}
              value={fieldId}
              onChange={handleFieldChange}
              placeholder={t('selectAdditionalField')}
            />
          </FormItem>
          {fieldId && (
            <>
              <Separator weight={2} />
              <CheckboxInput label={t('autofill.editable')} value={editable} onChange={handleEditableChange} />
              <Separator weight={1} />
              <CheckboxInput label={t('autofill.updateBase')} value={updateBase} onChange={handleUpdateBaseChange} />
              {updateBase && (
                <>
                  <Separator weight={1} />
                  <SmallText color={COLORS.dark4}>{t('autofill.updateBaseWarning')}</SmallText>
                </>
              )}
            </>
          )}
        </>
      )}

      {autofill && (
        <>
          <Separator weight={4} />
          <Link onClick={clearAutofill}>
            <SmallText bold color={COLORS.red}>
              {t('autofill.removeConfig')}
            </SmallText>
          </Link>
        </>
      )}

      <Separator weight={4} />
    </>
  )
}

export default AutofillSection
